/* eslint-disable max-lines */
import {
  InsuranceCompanyGroupTypeEnum,
  PaperVerificationStatusEnum,
  PathRequestHistoryEventTypeEnum,
  PathRequestPartStatusEnum,
  PathRequestPartTypeEnum,
  PathRequestProgressStatusEnum,
} from '@elearning-platform/api-types/enums';
import { BaseLabelData, Color, LabelColorsData } from '@elearning-platform/ui';
import { useCallback } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { ViewImage } from '../enums';
import {
  ContextTranslation,
  InsuranceCompanyLogos,
  PathRequestPartTypeLabels,
  UsePathRequests,
  UsePathRequestStatusBoxCommonConfig,
} from '../interfaces';

import { usePathRequestStatusBoxCommonConfig } from './usePathRequestStatusBoxCommonConfig.hook';

export const usePathRequest: () => UsePathRequests = (): UsePathRequests => {
  const { t }: TransProps<never> = useTranslation();
  const { getPathRequestPartTypeDescriptions }: UsePathRequestStatusBoxCommonConfig = usePathRequestStatusBoxCommonConfig();

  const getICLogo: (item: InsuranceCompanyGroupTypeEnum) => InsuranceCompanyLogos = useCallback((
    item: InsuranceCompanyGroupTypeEnum
  ): InsuranceCompanyLogos => {
    switch (item) {
      case InsuranceCompanyGroupTypeEnum.Allianz:
        return {
          dark: ViewImage.CommonICLogoAllianzDark,
          outlined: ViewImage.CommonICLogoAllianzOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Cardif:
        return {
          dark: ViewImage.CommonICLogoCardifDark,
          outlined: ViewImage.CommonICLogoCardifOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Compensa:
        return {
          dark: ViewImage.CommonICLogoCompensaDark,
          outlined: ViewImage.CommonICLogoCompensaOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Hestia:
        return {
          dark: ViewImage.CommonICLogoHestiaDark,
          outlined: ViewImage.CommonICLogoHestiaOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Pzu:
        return {
          dark: ViewImage.CommonICLogoPzuDark,
          outlined: ViewImage.CommonICLogoPzuOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Warta:
        return {
          dark: ViewImage.CommonICLogoWartaDark,
          outlined: ViewImage.CommonICLogoWartaOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Axa:
        return {
          dark: ViewImage.CommonICLogoAxaDark,
          outlined: ViewImage.CommonICLogoAxaOutlined,
        };
      case InsuranceCompanyGroupTypeEnum.Helvetia:
        return {
          dark: ViewImage.CommonICLogoHelvetiaDark,
          outlined: ViewImage.CommonICLogoHelvetiaOutlined,
        };
      default:
        // eslint-disable-next-line no-case-declarations
        const _: never = item;

        return { dark: _, outlined: _ };
    }
  }, []);

  const getLabelsByPathRequestProgressStatusEnum: (status: PathRequestProgressStatusEnum) => PathRequestPartTypeLabels = useCallback((
    status: PathRequestProgressStatusEnum
  ): PathRequestPartTypeLabels => {
    switch (status) {
      case PathRequestProgressStatusEnum.AuthorizationUploadOpen:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.authorizationUploadOpen.status'),
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.authorizationUploadOpen.task'),
        };
      case PathRequestProgressStatusEnum.DataFormInProgress:
      case PathRequestProgressStatusEnum.DataFormInRetakeProgress:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormInProgress.status'),
        };
      case PathRequestProgressStatusEnum.DataFormInRetakeReview:
      case PathRequestProgressStatusEnum.DataFormInReview:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormWaitingForReview.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormInReview.status'),
        };
      case PathRequestProgressStatusEnum.DataFormRejected:
      case PathRequestProgressStatusEnum.DataFormRejectedInRetake:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormRejected.status'),
        };
      case PathRequestProgressStatusEnum.ExamRegistrationOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakeOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRescheduleOpen:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.examRegistrationOpen.status'),
        };
      case PathRequestProgressStatusEnum.DataFormOpen:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormOpen.status'),
        };
      case PathRequestProgressStatusEnum.ExamRegistrationPending:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakePending:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.examRegistrationPending.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.examRegistrationPending.status'),
        };
      case PathRequestProgressStatusEnum.ExamResultsInReview:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.examResultsInReview.status'),
        };
      case PathRequestProgressStatusEnum.ExamResultsOpen:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.examResultsOpen.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.examResultsOpen.status'),
        };
      case PathRequestProgressStatusEnum.DataFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForReview:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormWaitingForReview.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.dataFormWaitingForReview.status'),
        };
      case PathRequestProgressStatusEnum.PreFormInProgress:
      case PathRequestProgressStatusEnum.PreFormInRetakeProgress:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormInProgress.status'),
        };
      case PathRequestProgressStatusEnum.PreFormInRetakeReview:
      case PathRequestProgressStatusEnum.PreFormInReview:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormWaitingForReview.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormInReview.status'),
        };
      case PathRequestProgressStatusEnum.PreFormOpen:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormOpen.status'),
        };
      case PathRequestProgressStatusEnum.PreFormRejected:
      case PathRequestProgressStatusEnum.PreFormRejectedInRetake:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormRejected.status'),
        };
      case PathRequestProgressStatusEnum.PreFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.PreFormWaitingForReview:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormWaitingForReview.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.preFormWaitingForReview.status'),
        };
      case PathRequestProgressStatusEnum.FileDownloadPending:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.fileDownloadPending.status'),
        };
      case PathRequestProgressStatusEnum.Finished:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.finished.status'),
        };
      case PathRequestProgressStatusEnum.UploadFormInProgress:
      case PathRequestProgressStatusEnum.UploadFormInRetakeProgress:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormInProgress.status'),
        };
      case PathRequestProgressStatusEnum.UploadFormOpen:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormOpen.status'),
        };
      case PathRequestProgressStatusEnum.UploadFormWaitingForReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForRetakeReview:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormWaitingForReview.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormWaitingForReview.status'),
        };
      case PathRequestProgressStatusEnum.UploadFormInReview:
      case PathRequestProgressStatusEnum.UploadFormInRetakeReview:
        return {
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormInReview.status'),
        };
      case PathRequestProgressStatusEnum.UploadFormRejected:
      case PathRequestProgressStatusEnum.UploadFormRejectedInRetake:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormWaitingForReview.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.uploadFormRejected.status'),
        };
      case PathRequestProgressStatusEnum.CourseMaterialsOpen:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.courseMaterialsOpen.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.courseMaterialsOpen.status'),
        };
      case PathRequestProgressStatusEnum.SystemProcessing:
        return {
          tasksListLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.systemProcessing.task'),
          statusLabel: t('toyota.app.pathRequests.usePathRequests.pathRequestStatus.systemProcessing.status'),
        };
      default:
        status satisfies never;

        return {
          statusLabel: status,
          tasksListLabel: status,
        };
    }
  }, [t]);

  const getLabelDataByPathRequestPartStatusEnum: (status: PathRequestPartStatusEnum) => BaseLabelData = (
    status: PathRequestPartStatusEnum
  ): BaseLabelData => {
    switch (status) {
      case PathRequestPartStatusEnum.Finished:
        return {
          colorsData: { textColor: Color.Green300, backgroundColor: Color.Green100 },
          content: t('toyota.app.pathRequests.usePathRequests.pathRequestPartStatus.finished'),
        };
      case PathRequestPartStatusEnum.InProgress:
      case PathRequestPartStatusEnum.InRetakeProgress:
        return {
          colorsData: { textColor: Color.Blue500, backgroundColor: Color.Blue100 },
          content: t('toyota.app.pathRequests.usePathRequests.pathRequestPartStatus.inProgress'),
        };
      case PathRequestPartStatusEnum.InReview:
      case PathRequestPartStatusEnum.InRetakeReview:
      case PathRequestPartStatusEnum.WaitingForRetakeReview:
      case PathRequestPartStatusEnum.WaitingForReview:
        return {
          colorsData: { textColor: Color.Yellow900, backgroundColor: Color.Yellow100 },
          content: t('toyota.app.pathRequests.usePathRequests.pathRequestPartStatus.inReview'),
        };
      case PathRequestPartStatusEnum.Rejected:
      case PathRequestPartStatusEnum.RejectedInRetake:
        return {
          colorsData: { textColor: Color.Red900, backgroundColor: Color.Red100 },
          content: t('toyota.app.pathRequests.usePathRequests.pathRequestPartStatus.rejected'),
        };
      case PathRequestPartStatusEnum.Open:
      case PathRequestPartStatusEnum.InRetakeOpen:
      case PathRequestPartStatusEnum.InRescheduleOpen:
        return {
          colorsData: { textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100 },
          content: t('toyota.app.pathRequests.usePathRequests.pathRequestPartStatus.open'),
        };
      case PathRequestPartStatusEnum.Initialized:
        return {
          colorsData: { textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100 },
          content: t('toyota.app.pathRequests.usePathRequests.pathRequestPartStatus.initialized'),
        };
      default:
        status satisfies never;

        return {
          colorsData: { textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100 },
          content: status,
        };
    }
  };

  const getLabelDataByPaperVerificationStatusEnum: (status: PaperVerificationStatusEnum) => BaseLabelData = (
    status: PaperVerificationStatusEnum
  ): BaseLabelData => {
    switch (status) {
      case PaperVerificationStatusEnum.Accepted:
        return {
          colorsData: { textColor: Color.Green300, backgroundColor: Color.Green100 },
          content: t('toyota.app.pathRequests.usePathRequests.paperVerificationStatus.documentsAccepted'),
        };
      case PaperVerificationStatusEnum.DocumentsRejected:
      case PaperVerificationStatusEnum.PackageRejected:
        return {
          colorsData: { textColor: Color.Red900, backgroundColor: Color.Red100 },
          content: t(`toyota.app.pathRequests.usePathRequests.paperVerificationStatus.${
            status === PaperVerificationStatusEnum.DocumentsRejected
              ? 'documentsRejected'
              : 'packageRejected'
          }`),
        };
      case PaperVerificationStatusEnum.WaitingForReview:
        return {
          colorsData: { textColor: Color.Yellow900, backgroundColor: Color.Yellow100 },
          content: t('toyota.app.pathRequests.usePathRequests.paperVerificationStatus.documentsUnderReview'),
        };
      case PaperVerificationStatusEnum.AwaitingDocuments:
        return {
          colorsData: { textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100 },
          content: t('toyota.app.pathRequests.usePathRequests.paperVerificationStatus.documentsAwaiting'),
        };
      default:
        status satisfies never;

        return {
          colorsData: { textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100 },
          content: status,
        };
    }
  };

  const transformPathRequestProgressStatusEnumToBarTableCellLabel: (item: PathRequestProgressStatusEnum) => LabelColorsData = (
    item: PathRequestProgressStatusEnum
  ): LabelColorsData => {
    switch (item) {
      case PathRequestProgressStatusEnum.DataFormInRetakeReview:
      case PathRequestProgressStatusEnum.DataFormInReview:
      case PathRequestProgressStatusEnum.UploadFormInRetakeReview:
      case PathRequestProgressStatusEnum.UploadFormInReview:
      case PathRequestProgressStatusEnum.ExamResultsInReview:
      case PathRequestProgressStatusEnum.PreFormInReview:
      case PathRequestProgressStatusEnum.PreFormInRetakeReview:
      case PathRequestProgressStatusEnum.PreFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.PreFormWaitingForReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForReview:
      case PathRequestProgressStatusEnum.DataFormWaitingForRetakeReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForReview:
      case PathRequestProgressStatusEnum.UploadFormWaitingForRetakeReview:
        return {
          textColor: Color.Yellow900, backgroundColor: Color.Yellow100,
        };
      case PathRequestProgressStatusEnum.DataFormInRetakeProgress:
      case PathRequestProgressStatusEnum.DataFormInProgress:
      case PathRequestProgressStatusEnum.PreFormInRetakeProgress:
      case PathRequestProgressStatusEnum.PreFormInProgress:
      case PathRequestProgressStatusEnum.UploadFormInProgress:
      case PathRequestProgressStatusEnum.UploadFormInRetakeProgress:
      case PathRequestProgressStatusEnum.FileDownloadPending:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakePending:
      case PathRequestProgressStatusEnum.ExamRegistrationPending:
        return {
          textColor: Color.Blue500, backgroundColor: Color.Blue100,
        };
      case PathRequestProgressStatusEnum.Finished:
        return {
          textColor: Color.Green300, backgroundColor: Color.Green100,
        };
      case PathRequestProgressStatusEnum.DataFormRejectedInRetake:
      case PathRequestProgressStatusEnum.DataFormRejected:
      case PathRequestProgressStatusEnum.UploadFormRejected:
      case PathRequestProgressStatusEnum.UploadFormRejectedInRetake:
      case PathRequestProgressStatusEnum.PreFormRejected:
      case PathRequestProgressStatusEnum.PreFormRejectedInRetake:
        return {
          textColor: Color.Red900, backgroundColor: Color.Red100,
        };
      case PathRequestProgressStatusEnum.PreFormOpen:
      case PathRequestProgressStatusEnum.DataFormOpen:
      case PathRequestProgressStatusEnum.AuthorizationUploadOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationOpen:
      case PathRequestProgressStatusEnum.ExamResultsOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRetakeOpen:
      case PathRequestProgressStatusEnum.ExamRegistrationRescheduleOpen:
      case PathRequestProgressStatusEnum.UploadFormOpen:
      case PathRequestProgressStatusEnum.CourseMaterialsOpen:
      case PathRequestProgressStatusEnum.SystemProcessing:
        return {
          textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100,
        };
      default:
        // eslint-disable-next-line no-case-declarations
        const _: never = item;

        return {
          textColor: Color.Gray800, backgroundColor: Color.White, borderColor: Color.Black100,
        };
    }
  };

  const pathRequestHistoryItemsLabels: Record<PathRequestPartTypeEnum | PathRequestHistoryEventTypeEnum, ContextTranslation> = {
    ...Object.values(PathRequestPartTypeEnum).reduce(
      (
        acc: Record<PathRequestPartTypeEnum, ContextTranslation>,
        type: PathRequestPartTypeEnum
      ): Record<PathRequestPartTypeEnum, ContextTranslation> => ({
        ...acc, [type]: getPathRequestPartTypeDescriptions(type),
      }),
      {} as Record<PathRequestPartTypeEnum, ContextTranslation>,
    ),
    // TODO: adjust translations when real custom events created
    [PathRequestHistoryEventTypeEnum.CustomEvent]: {
      title: 'testowy custom event',
      tooltipText: 'textowy tooltip dla custom event',
    },
  };

  // TODO: get labels directly from be when api will be available in toyota
  const insuranceCompaniesTranslationsMap: Record<InsuranceCompanyGroupTypeEnum, string> = {
    [InsuranceCompanyGroupTypeEnum.Warta]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.warta'),
    [InsuranceCompanyGroupTypeEnum.Cardif]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.cardif'),
    [InsuranceCompanyGroupTypeEnum.Compensa]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.compensa'),
    [InsuranceCompanyGroupTypeEnum.Hestia]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.hestia'),
    [InsuranceCompanyGroupTypeEnum.Allianz]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.allianz'),
    [InsuranceCompanyGroupTypeEnum.Pzu]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.pzu'),
    [InsuranceCompanyGroupTypeEnum.Axa]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.axa'),
    [InsuranceCompanyGroupTypeEnum.Helvetia]: t('toyota.app.pathRequests.usePathRequests.insuranceCompanyTypes.helvetia'),
  };

  return {
    transformPathRequestProgressStatusEnumToBarTableCellLabel,
    getLabelsByPathRequestProgressStatusEnum,
    getICLogo,
    getLabelDataByPathRequestPartStatusEnum,
    pathRequestHistoryItemsLabels,
    insuranceCompaniesTranslationsMap,
    getLabelDataByPaperVerificationStatusEnum,
  };
};
